import { doc, getDoc, updateDoc, collection, getDocs, QueryDocumentSnapshot } from 'firebase/firestore';
import type { DocumentData } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface BusinessData {
  id: string;
  companyName: string;
  businessId: string;
  businessIdType: string;
  address: string;
  phone: string;
  businessEmail: string;
  employeeCount: number;
  status: string;
  logo?: string;
  description?: string;
  services?: string;
  taxes?: string;
  employeeRegistrationCode?: string;
}

export interface ClientContact {
  id: string;
  name: string;
  email: string;
  phone: string;
  linkedinUrl?: string;
}

export interface ClientData {
  id: string;
  name: string;
  address: string;
  email: string;
  phone: string;
  logo?: string;
  contacts?: ClientContact[];
  createdAt?: any;
  updatedAt?: any;
}

export interface ProjectData {
  id: string;
  name: string;
  status: string;
  progress: number;
  tasks?: any[]; // Array de tareas del proyecto
  // Puedes agregar más campos si los tienes en Firestore
}

export interface EmployeeData {
  id: string;
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
  position: string;
  role: string;
  status: 'pending' | 'active' | 'inactive';
  createdAt: any;
  updatedAt: any;
}

export const getBusinessProfile = async (userId: string): Promise<BusinessData> => {
  try {
    // Obtener el ID de la empresa del usuario
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.data();
    
    if (!userData?.businessId) {
      throw new Error('No business associated with this user');
    }

    // Obtener los datos de la empresa
    const businessDoc = await getDoc(doc(db, 'businesses', userData.businessId));
    if (!businessDoc.exists()) {
      throw new Error('Business not found');
    }

    return { id: businessDoc.id, ...businessDoc.data() } as BusinessData;
  } catch (error) {
    console.error('Error loading business data:', error);
    throw error;
  }
};

export const updateBusinessProfile = async (userId: string, updateData: Partial<BusinessData>): Promise<void> => {
  try {
    // Obtener el ID de la empresa del usuario
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.data();
    
    if (!userData?.businessId) {
      throw new Error('No business associated with this user');
    }

    // Actualizar los datos de la empresa
    const businessRef = doc(db, 'businesses', userData.businessId);
    await updateDoc(businessRef, updateData);
  } catch (error) {
    console.error('Error updating business data:', error);
    throw error;
  }
};

export const getBusinessClients = async (businessId: string): Promise<ClientData[]> => {
  try {
    const clientsCol = collection(db, 'clients', businessId, 'clients');
    const snapshot = await getDocs(clientsCol);
    console.log("Docs obtenidos de Firestore (clients):", snapshot.docs.map(d => d.data()));
    return snapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({
      ...doc.data(),
      id: doc.id,
    })) as ClientData[];
  } catch (error) {
    console.error('Error loading clients:', error);
    throw error;
  }
};

export const getBusinessProjects = async (businessId: string): Promise<ProjectData[]> => {
  try {
    const projectsCol = collection(db, 'projects', businessId, 'projects');
    const snapshot = await getDocs(projectsCol);
    console.log("Docs obtenidos de Firestore (projects):", snapshot.docs.map(d => d.data()));
    
    // Obtener proyectos con sus tareas
    const projectsWithTasks = await Promise.all(
      snapshot.docs.map(async (projectDoc: QueryDocumentSnapshot<DocumentData>) => {
        const projectData = projectDoc.data();
        const projectId = projectDoc.id;
        
        // Obtener las tareas de cada proyecto
        try {
          const tasksCol = collection(db, 'projects', businessId, 'projects', projectId, 'tasks');
          const tasksSnapshot = await getDocs(tasksCol);
          const tasks = tasksSnapshot.docs.map(taskDoc => ({
            ...taskDoc.data(),
            id: taskDoc.id,
          }));
          
          return {
            ...projectData,
            id: projectId,
            tasks: tasks,
          };
        } catch (error) {
          console.error(`Error loading tasks for project ${projectId}:`, error);
          return {
            ...projectData,
            id: projectId,
            tasks: [],
          };
        }
      })
    );
    
    return projectsWithTasks as ProjectData[];
  } catch (error) {
    console.error('Error loading projects:', error);
    throw error;
  }
};

export const getBusinessEmployees = async (businessId: string): Promise<EmployeeData[]> => {
  try {
    const usersCol = collection(db, 'users');
    const snapshot = await getDocs(usersCol);
    
    // Filtrar usuarios que pertenecen a este negocio y tienen rol employee
    const employees = snapshot.docs
      .map((doc: QueryDocumentSnapshot<DocumentData>) => ({
        ...doc.data(),
        id: doc.id,
      }))
      .filter((user: any) => user.businessId === businessId && user.role === 'employee');
    
    return employees as EmployeeData[];
  } catch (error) {
    console.error('Error loading employees:', error);
    throw error;
  }
};

export const updateEmployeeStatus = async (userId: string, status: 'pending' | 'active' | 'inactive'): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      status,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating employee status:', error);
    throw error;
  }
};

export const generateRegistrationCode = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

export const updateBusinessClient = async (businessId: string, clientId: string, updateData: Partial<ClientData>): Promise<void> => {
  try {
    const clientRef = doc(db, 'clients', businessId, 'clients', clientId);
    
    // Limpiar campos undefined antes de guardar
    const cleanData: any = { updatedAt: new Date() };
    Object.keys(updateData).forEach(key => {
      const value = updateData[key as keyof ClientData];
      if (value !== undefined) {
        cleanData[key] = value;
      }
    });
    
    await updateDoc(clientRef, cleanData);
  } catch (error) {
    console.error('Error updating client:', error);
    throw error;
  }
}; 